import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
  IconButton,
  CircularProgress,
  Card,
  CardContent,
  Container,
  Stack,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  useTheme,
  useMediaQuery,
  Skeleton,
  List,
  ListItem,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  CloudUpload,
  Delete,
  AddCircleOutline,
  Quiz,
  SmartToy,
} from "@mui/icons-material";

import Layout from "../../common/Layout";
import { useNotification } from "../../../context/NotificationContext";
import apiClient from "../../../context/apiClient";

import EducationStep from "./EducationStep";
import ProjectsStep from "./ProjectsStep";
import ProfessionalDetailsStep from "./ProfessionalDetailsStep";
import SkillsCertsStep from "./SkillsCertsStep";
import AchievementsStep from "./AchievementsStep";
import PreferencesStep from "./PreferencesStep";
import DocsLinksStep from "./DocsLinksStep";
import AISummaryStep from "./AISummaryStep";
import PersonalInfoStep from "./PersonalInfoStep";

const initialExperienceState = {
  title: "",
  company: "",
  duration: "",
  responsibilities: [],
};

const initialInternshipState = {
  title: "",
  company: "",
  duration: "",
  responsibilities: [],
};

const initialDegreeState = {
  degree_name: "",
  institution_name: "",
  specialization: "",
  year_passing: "",
  score: "",
  certificate: null,
};

const initialPostGradState = {
  course_name: "",
  institution_name: "",
  specialization: "",
  year_passing: "",
  score: "",
  certificate: null,
};

const initialDiplomaState = {
  course_name: "",
  institution_name: "",
  specialization: "",
  year_passing: "",
  score: "",
  certificate: null,
};

const initialCertificationState = {
  name: "",
  issuer: "",
  date: "",
};

const initialPublicationState = { name: "", link: "" };
const initialWorkAuthState = { type: "", country: "", valid_till: "" };
const initialLinkState = { platform: "", link: "" };
const initialFrameworkToolState = { name: "" };
const initialWorkPreferenceState = { location: "", preference: "" };

const validateStep = (step, profileData) => {
  const errors = {};
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  switch (step) {
    case 0:
      if (!profileData.name?.trim()) errors.name = "Name is required";
      if (!profileData.email?.trim()) {
        errors.email = "Email is required";
      } else if (!emailRegex.test(profileData.email)) {
        errors.email = "Enter a valid email address";
      }
      if (!profileData.phone?.trim()) {
        errors.phone = "Phone number is required";
      } else if (profileData.phone.length !== 10) {
        errors.phone = "Phone number must be 10 digits";
      }
      if (!profileData.profile_photo && !profileData.profile_photo_url) {
        errors.profile_photo = "Profile photo is required";
      }
      if (!profileData.current_area?.trim())
        errors.current_area = "Area/Locality is required";
      if (!profileData.current_city?.trim())
        errors.current_city = "City is required";
      if (!profileData.current_district?.trim())
        errors.current_district = "District is required";
      if (!profileData.current_state?.trim())
        errors.current_state = "State is required";
      if (!profileData.current_country?.trim())
        errors.current_country = "Country is required";
      if (!profileData.current_pincode?.trim()) {
        errors.current_pincode = "Pincode is required";
      } else if (profileData.current_pincode.length !== 6) {
        errors.current_pincode = "Pincode must be 6 digits";
      }
      if (!profileData.isSameAddress) {
        if (!profileData.permanent_area?.trim())
          errors.permanent_area = "Area/Locality is required";
        if (!profileData.permanent_city?.trim())
          errors.permanent_city = "City is required";
        if (!profileData.permanent_district?.trim())
          errors.permanent_district = "District is required";
        if (!profileData.permanent_state?.trim())
          errors.permanent_state = "State is required";
        if (!profileData.permanent_country?.trim())
          errors.permanent_country = "Country is required";
        if (!profileData.permanent_pincode?.trim()) {
          errors.permanent_pincode = "Pincode is required";
        } else if (profileData.permanent_pincode.length !== 6) {
          errors.permanent_pincode = "Pincode must be 6 digits";
        }
      }
      break;

    case 1:
      if (!profileData.tenth_school_name?.trim())
        errors.tenth_school_name = "10th school name is required";
      if (!profileData.tenth_board_name?.trim())
        errors.tenth_board_name = "10th board name is required";
      if (!profileData.tenth_year_passing?.trim())
        errors.tenth_year_passing = "10th year of passing is required";
      if (!profileData.tenth_score?.trim())
        errors.tenth_score = "10th score is required";

      if (profileData.degree_details?.length === 0) {
        errors.education = "At least one graduation degree is required";
      }

      const has12th =
        profileData.twelfth_college_name?.trim() ||
        profileData.twelfth_board_name?.trim() ||
        profileData.twelfth_year_passing?.trim() ||
        profileData.twelfth_score?.trim();
      const hasDiploma = profileData.diploma_details?.length > 0;

      if (!has12th && !hasDiploma && !errors.education) {
        errors.education =
          "Either 12th (HSC) details or at least one Diploma is required";
      }
      break;

    case 3:
      if (
        !profileData.is_fresher &&
        profileData.experience?.length === 0 &&
        profileData.internships?.length === 0
      ) {
        errors.professional =
          "If not a fresher, at least one professional experience or internship is required.";
      }
      break;

    case 4:
      if (!profileData.skills || profileData.skills.length === 0) {
        errors.skills = "At least one technical skill is required.";
      }
      break;

    case 6:
      if (
        !profileData.domain_interest ||
        profileData.domain_interest.length === 0
      ) {
        errors.domain_interest =
          "At least one domain interest must be selected.";
      }
      if (
        !profileData.work_preferences ||
        profileData.work_preferences.length === 0
      ) {
        errors.work_preferences = "At least one work preference must be added.";
      }
      if (
        !profileData.work_authorizations ||
        profileData.work_authorizations.length === 0
      ) {
        errors.work_authorizations =
          "At least one work authorization must be added.";
      }
      break;
    default:
      break;
  }

  return errors;
};

const saveToLocalStorage = (data, step, userId) => {
  if (!userId) return;
  try {
    const storableData = JSON.stringify(data, (key, value) => {
      if (value instanceof File) {
        return {
          _isAFile: true,
          name: value.name,
          size: value.size,
          type: value.type,
        };
      }
      return value;
    });
    localStorage.setItem(`profileBuilderData_${userId}`, storableData);
    localStorage.setItem(`profileBuilderStep_${userId}`, step.toString());
  } catch (error) {
    console.error("Error saving to localStorage:", error);
  }
};

const loadFromLocalStorage = (userId) => {
  if (!userId) return { data: null, step: 0 };
  try {
    const data = localStorage.getItem(`profileBuilderData_${userId}`);
    const step = localStorage.getItem(`profileBuilderStep_${userId}`);
    if (!data) return { data: null, step: step ? parseInt(step) : 0 };

    const parsedData = JSON.parse(data, (key, value) => {
      if (value && value._isAFile) {
        return new File([], value.name, { type: value.type });
      }
      return value;
    });

    return {
      data: parsedData,
      step: step ? parseInt(step) : 0,
    };
  } catch (error) {
    console.error("Error loading from localStorage:", error);
    return { data: null, step: 0 };
  }
};

const clearLocalStorage = (userId) => {
  if (!userId) return;
  try {
    localStorage.removeItem(`profileBuilderData_${userId}`);
    localStorage.removeItem(`profileBuilderStep_${userId}`);
  } catch (error) {
    console.error("Error clearing localStorage:", error);
  }
};

const DynamicListSection = ({
  title,
  field,
  items,
  formState,
  setFormState,
  initialState,
  formFields,
  renderItem,
  handleAddItem,
  handleDeleteItem,
  showNotification,
  children,
  isAdding,
}) => {
  const handleAdd = () => {
    if (field === "certification_details") {
      handleAddItem();
    } else {
      if (formFields[0].required !== false && !formState[formFields[0].name]) {
        showNotification(`${formFields[0].label} is required.`, "error");
        return;
      }
      handleAddItem(field, formState);
      setFormState(initialState);
    }
  };

  return (
    <Box
      sx={{
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
        border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
        borderRadius: "16px",
        p: 3,
        mb: 3,
        transition: "border-color 0.25s ease",
        "&:hover": { borderColor: (theme) => alpha(theme.palette.primary.main, 0.3) },
      }}
    >
      <Typography variant="subtitle1" fontWeight={800} gutterBottom sx={{ color: "primary.main", letterSpacing: "0.04em" }}>
        {title}
      </Typography>
      <Grid container spacing={2}>
        {formFields.map((f) => (
          <Grid item xs={12} sm={formFields.length > 2 ? 6 : 12} key={f.name}>
            {f.type === "select" ? (
              <FormControl fullWidth size="small">
                <InputLabel>{f.label}</InputLabel>
                <Select
                  value={formState[f.name] || ""}
                  onChange={(e) =>
                    setFormState((prev) => ({
                      ...prev,
                      [f.name]: e.target.value,
                    }))
                  }
                  label={f.label}
                >
                  {f.options?.map((option) => (
                    <MenuItem
                      key={option.value || option}
                      value={option.value || option}
                    >
                      {option.label || option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : f.type === "autocomplete" ? (
              <Autocomplete
                fullWidth
                freeSolo
                size="small"
                options={f.options || []}
                value={formState[f.name] || ""}
                onInputChange={(event, newInputValue) => {
                  setFormState((prev) => ({
                    ...prev,
                    [f.name]: newInputValue,
                  }));
                }}
                renderInput={(params) => (
                  <TextField {...params} label={f.label} />
                )}
              />
            ) : f.type === "file" ? (
              <Box>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<CloudUpload />}
                  size="small"
                  fullWidth
                >
                  {formState[f.name] ? `Change ${f.label}` : f.label}
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setFormState((prev) => ({
                          ...prev,
                          [f.name]: file,
                        }));
                      }
                    }}
                  />
                </Button>
                {formState[f.name] && (
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Selected: {formState[f.name].name}
                  </Typography>
                )}
              </Box>
            ) : (
              <TextField
                fullWidth
                label={f.label}
                value={formState[f.name] || ""}
                onChange={(e) =>
                  setFormState((prev) => ({
                    ...prev,
                    [f.name]: e.target.value,
                  }))
                }
                size="small"
                variant="outlined"
                type={f.type || "text"}
                placeholder={f.placeholder}
                required={f.required}
              />
            )}
          </Grid>
        ))}
      </Grid>
      {children}
      <Button
        onClick={handleAdd}
        variant="contained"
        size="small"
        startIcon={isAdding ? null : <AddCircleOutline />}
        sx={{ mt: 2, borderRadius: "10px", textTransform: "none", fontWeight: 700 }}
        disabled={isAdding}
      >
        {isAdding ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          `Add ${title.slice(0, -1)}`
        )}
      </Button>
      <Divider sx={{ my: 2, borderColor: (theme) => alpha(theme.palette.primary.main, 0.2) }} />
      <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ color: "text.secondary", fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>
        Added {title} ({items.length})
      </Typography>
      {items.length > 0 ? (
        <List dense>
          {items.map((item, index) => (
            <ListItem
              key={index}
              secondaryAction={
                <IconButton
                  edge="end"
                  aria-label="delete"
                  onClick={() => handleDeleteItem(field, index)}
                  color="error"
                >
                  <Delete />
                </IconButton>
              }
              sx={{
                border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                borderRadius: "10px",
                mb: 1,
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
              }}
            >
              {renderItem(item, index)}
            </ListItem>
          ))}
        </List>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No {title.toLowerCase()} added yet.
        </Typography>
      )}
    </Box>
  );
};

const ProfileBuilder = () => {
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const theme = useTheme();
  const isTabletOrSmaller = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const formRef = useRef(null);

  const [activeStep, setActiveStep] = useState(0);
  const [validationErrors, setValidationErrors] = useState({});

  const [isCompletionModalOpen, setIsCompletionModalOpen] = useState(false);

  const initialProfileState = {
    name: "",
    email: "",
    phone: "",
    summary: "",
    generated_summary: "",
    current_location: "",
    current_area: "",
    current_city: "",
    current_district: "",
    current_state: "",
    current_country: "",
    current_pincode: "",
    permanent_area: "",
    permanent_city: "",
    permanent_district: "",
    permanent_state: "",
    permanent_country: "",
    permanent_pincode: "",
    current_company: "",
    profile_photo: null,
    profile_photo_url: "",
    work_arrangement: "",
    preferred_location: [],
    preferred_tech_stack: "",
    dev_environment: "",
    preferences: [],
    work_preferences: [],
    volunteering_experience: "",
    extracurriculars: "",
    tenth_board_name: "",
    tenth_school_name: "",
    tenth_year_passing: "",
    tenth_score: "",
    twelfth_board_name: "",
    twelfth_college_name: "",
    twelfth_year_passing: "",
    twelfth_score: "",
    skills: [],
    experience: [],
    internships: [],
    projects: [],
    awards: [],
    open_source_contributions: [],
    interests: [],
    references: [],
    languages: {},
    frameworks_tools: [],
    ui_documents: {
      resume: null,
      portfolio: null,
      resume_url: "",
      portfolio_url: "",
    },
    uploaded_documents: [],
    is_experienced: false,
    has_internship: false,
    is_fresher: true,
    professional_links: [],
    publications: [],
    degree_details: [],
    post_graduate_details: [],
    diploma_details: [],
    certification_details: [],
    work_authorizations: [],
    domain_interest: [],
    employee_level: [],
  };

  const [profileData, setProfileData] = useState(initialProfileState);
  const [profileProgress, setProfileProgress] = useState({
    progress_percentage: 0,
    message: "Loading profile progress...",
    missing_sections: [],
  });

  const [profileId, setProfileId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploadingResume, setIsUploadingResume] = useState(false);
  const [isSameAddress, setIsSameAddress] = useState(false);
  const [isFetchingPincode, setIsFetchingPincode] = useState({
    current: false,
    permanent: false,
  });

  const [currentExperience, setCurrentExperience] = useState(
    initialExperienceState
  );
  const [currentInternship, setCurrentInternship] = useState(
    initialInternshipState
  );
  const [editingExperienceIndex, setEditingExperienceIndex] = useState(null);
  const [editingInternshipIndex, setEditingInternshipIndex] = useState(null);
  const [newResponsibility, setNewResponsibility] = useState("");
  const [newInternshipResponsibility, setNewInternshipResponsibility] =
    useState("");

  const [currentDegree, setCurrentDegree] = useState(initialDegreeState);
  const [currentPostGrad, setCurrentPostGrad] = useState(initialPostGradState);
  const [currentDiploma, setCurrentDiploma] = useState(initialDiplomaState);
  const [currentCertification, setCurrentCertification] = useState(
    initialCertificationState
  );
  const [currentPublication, setCurrentPublication] = useState(
    initialPublicationState
  );
  const [currentWorkAuth, setCurrentWorkAuth] = useState(initialWorkAuthState);
  const [currentLink, setCurrentLink] = useState(initialLinkState);
  const [currentFrameworkTool, setCurrentFrameworkTool] = useState(
    initialFrameworkToolState
  );
  const [currentWorkPreference, setCurrentWorkPreference] = useState(
    initialWorkPreferenceState
  );

  const [currentLanguage, setCurrentLanguage] = useState("");
  const [currentLangProficiency, setCurrentLangProficiency] = useState("");
  const [projectName, setProjectName] = useState("");
  const [projectDesc, setProjectDesc] = useState("");
  const [projectUrl, setProjectUrl] = useState("");

  const steps = [
    "Personal Information",
    "Education",
    "Projects",
    "Professional Details",
    "Skills & Certifications",
    "Achivements & More",
    "Preferrences",
    "Docs & Link",
    "AI Summary",
  ];

  useEffect(() => {
    loadExistingProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (profileId) {
      saveToLocalStorage(profileData, activeStep, profileId);
    }
  }, [profileData, activeStep, profileId]);

  useEffect(() => {
    if (isSameAddress) {
      setProfileData((prev) => ({
        ...prev,
        permanent_area: prev.current_area,
        permanent_city: prev.current_city,
        permanent_district: prev.current_district,
        permanent_state: prev.current_state,
        permanent_country: prev.current_country,
        permanent_pincode: prev.current_pincode,
      }));
    }
  }, [
    isSameAddress,
    profileData.current_area,
    profileData.current_city,
    profileData.current_district,
    profileData.current_state,
    profileData.current_country,
    profileData.current_pincode,
  ]);

  const scrollToTop = () => {
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const fetchProfileProgress = async () => {
    try {
      const response = await apiClient.get("/resume-progress/");
      if (response.data) {
        setProfileProgress(response.data);
      }
    } catch (error) {
      console.error("Error fetching profile progress:", error);
      setProfileProgress((prev) => ({
        ...prev,
        message: "Could not load progress details.",
      }));
    }
  };

  const loadExistingProfile = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get("/resume-builder/");
      let profileToLoad = null;

      if (response.data && response.data.length > 0) {
        profileToLoad = response.data[0];
      } else if (response.data && !Array.isArray(response.data)) {
        profileToLoad = response.data;
      }

      fetchProfileProgress();

      if (profileToLoad) {
        populateFormWithData(profileToLoad);

        const userId = profileToLoad.id || profileToLoad.resume_id;
        const { step: localStep } = loadFromLocalStorage(userId);

        setActiveStep(localStep || 0);
        setProfileId(userId);
      } else {
        console.log("No existing profile found on server, starting fresh.");
        setProfileData(initialProfileState);
        setActiveStep(0);
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        showNotification("Error loading existing profile", "error");
      } else {
        console.log("No existing profile found for this user.");
      }
      setProfileData(initialProfileState);
      setActiveStep(0);
    } finally {
      setIsLoading(false);
    }
  };

  const validateResumeFile = (file) => {
    if (!file) {
      showNotification("Please select a resume file first.", "error");
      return false;
    }
    if (file.type !== "application/pdf") {
      showNotification(
        "Only PDF files are allowed for resume upload.",
        "error"
      );
      return false;
    }
    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      showNotification("Resume file size must be less than 2MB.", "error");
      return false;
    }
    return true;
  };

  const handleResumeUpload = async (file) => {
    if (!validateResumeFile(file)) {
      return;
    }
    setIsUploadingResume(true);
    showNotification("Uploading resume and extracting data...", "info");
    const formData = new FormData();
    formData.append("resume_pdf", file);
    try {
      await apiClient.post("/resume-builder/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      showNotification(
        "Resume processed! Form will refresh with new data shortly.",
        "success"
      );
      setTimeout(() => {
        loadExistingProfile();
      }, 2000);
    } catch (error) {
      console.error("Resume upload error:", error);
      const errorMessage =
        error.response?.data?.detail ||
        "Failed to upload resume. Please try again.";
      showNotification(errorMessage, "error");
    } finally {
      setIsUploadingResume(false);
    }
  };

  const fetchAddressFromPincode = async (pincode, addressType) => {
    if (!pincode || pincode.length !== 6) {
      return;
    }
    setIsFetchingPincode((prev) => ({ ...prev, [addressType]: true }));
    try {
      const response = await fetch(
        `https://api.postalpincode.in/pincode/${pincode}`
      );
      const data = await response.json();
      if (data && data[0].Status === "Success") {
        const postOffice = data[0].PostOffice[0];
        const { District, State, Country } = postOffice;
        const City = postOffice.Name;
        setProfileData((prev) => ({
          ...prev,
          [`${addressType}_city`]: City,
          [`${addressType}_district`]: District,
          [`${addressType}_state`]: State,
          [`${addressType}_country`]: Country,
        }));
        showNotification("Address auto-filled successfully!", "success");
      } else {
        showNotification(data[0].Message || "Invalid Pincode.", "error");
      }
    } catch (error) {
      console.error("Error fetching pincode data:", error);
      showNotification(
        "Could not fetch address. Please enter manually.",
        "error"
      );
    } finally {
      setIsFetchingPincode((prev) => ({ ...prev, [addressType]: false }));
    }
  };

  const createProfileDataFromResponse = (data, currentData) => {
    const parseJSONField = (field) => {
      if (Array.isArray(field)) return field;
      if (typeof field === "string" && field.trim().startsWith("[")) {
        try {
          const validJSONString = field.replace(/'/g, '"');
          return JSON.parse(validJSONString);
        } catch (e) {
          console.warn(
            "Could not parse malformed JSON string, returning empty array:",
            field
          );
          return [];
        }
      }
      return field || [];
    };

    const parseJSONObject = (field) => {
      if (
        typeof field === "object" &&
        field !== null &&
        !Array.isArray(field)
      ) {
        return field;
      }
      if (typeof field === "string" && field.startsWith("{")) {
        try {
          return JSON.parse(field);
        } catch {
          return {};
        }
      }
      return field || {};
    };

    const parseAddress = (districtStr) => {
      if (!districtStr) return { district: "", pincode: "" };
      const match = districtStr.match(/(.*?)(?:-|\s)?(\d{6})$/);
      if (match) {
        const district = match[1].trim().replace(/[-]$/, "").trim();
        const pincode = match[2];
        return { district, pincode };
      }
      return { district: districtStr, pincode: "" };
    };

    const currentAddr = parseAddress(data.current_district);
    const permAddr = parseAddress(data.permanent_district);

    // CHANGED: Convert responsibilities string from API into an array for the UI
    const experience = (parseJSONField(data.experience) || []).map((exp) => ({
      ...exp,
      company: exp.company_name || exp.company || "",
      responsibilities:
        typeof exp.responsibilities === "string" &&
        exp.responsibilities.length > 0
          ? exp.responsibilities.split("\n")
          : [],
    }));
    const internships = (parseJSONField(data.internships) || []).map(
      (intern) => ({
        ...intern,
        company: intern.company_name || intern.company || "",
        responsibilities:
          typeof intern.responsibilities === "string" &&
          intern.responsibilities.length > 0
            ? intern.responsibilities.split("\n")
            : [],
      })
    );

    return {
      name: data.name || "",
      email: data.email || "",
      phone: data.phone || "",
      current_company: data.current_company || "",
      profile_photo: null,
      profile_photo_url: data.profile_photo_url || "",
      current_location: data.current_location || "",
      current_area: data.current_area || "",
      current_city: data.current_city || "",
      current_district: currentAddr.district,
      current_pincode: currentAddr.pincode,
      current_state: data.current_state || "",
      current_country: data.current_country || "",
      permanent_area: data.permanent_area || "",
      permanent_city: data.permanent_city || "",
      permanent_district: permAddr.district,
      permanent_pincode: permAddr.pincode,
      permanent_state: data.permanent_state || "",
      permanent_country: data.permanent_country || "",
      tenth_board_name: data.education_details?.tenth?.board_name || "",
      tenth_school_name: data.education_details?.tenth?.school_name || "",
      tenth_year_passing: data.education_details?.tenth?.year_passing || "",
      tenth_score: data.education_details?.tenth?.score || "",
      twelfth_board_name: data.education_details?.twelfth?.board_name || "",
      twelfth_college_name: data.education_details?.twelfth?.college_name || "",
      twelfth_year_passing: data.education_details?.twelfth?.year_passing || "",
      twelfth_score: data.education_details?.twelfth?.score || "",
      degree_details: parseJSONField(data.degree_details),
      post_graduate_details: parseJSONField(data.post_graduate_details),
      diploma_details: parseJSONField(data.diploma_details),
      certification_details: parseJSONField(data.certification_details),
      frameworks_tools: parseJSONField(data.frameworks_tools),
      experience: experience,
      internships: internships,
      projects: parseJSONField(data.projects),
      skills: parseJSONField(data.skills),
      awards: parseJSONField(data.awards),
      open_source_contributions: parseJSONField(data.open_source_contributions),
      interests: parseJSONField(data.interests),
      publications: parseJSONField(data.publications),
      references: parseJSONField(data.references),
      professional_links: (parseJSONField(data.professional_links) || []).map(
        (link) => ({
          platform: link.name,
          link: link.url,
        })
      ),
      work_authorizations: parseJSONField(data.work_authorizations),
      work_preferences: (parseJSONField(data.work_preferences) || []).map(
        (pref) => ({
          location: pref.location,
          preference: pref.arrangement,
        })
      ),
      languages: parseJSONObject(data.languages),
      preferences: Array.isArray(data.preferences) ? data.preferences : [],
      preferred_tech_stack: data.preferred_tech_stack || "",
      dev_environment: data.dev_environment || "",
      preferred_location: parseJSONField(data.preferred_location),
      work_arrangement: data.work_arrangement || "",
      volunteering_experience: data.volunteering_experience || "",
      extracurriculars: data.extracurriculars || "",
      summary: data.summary || "",
      generated_summary: data.generated_summary || "",
      is_experienced: experience.length > 0,
      has_internship: internships.length > 0,
      is_fresher:
        typeof data.is_fresher === "boolean"
          ? data.is_fresher
          : experience.length === 0 && internships.length === 0,
      domain_interest: parseJSONField(data.domain_interest),
      employee_level: parseJSONField(data.employee_level),
      ui_documents: {
        resume: null,
        portfolio: null,
        resume_url: data.resume_pdf_url || "",
        portfolio_url: data.portfolio_pdf_url || "",
        tenth_result_url:
          data.education_details?.tenth?.result_upload_url || "",
        twelfth_result_url:
          data.education_details?.twelfth?.result_upload_url || "",
      },
      uploaded_documents: parseJSONField(data.resume_documents),
    };
  };

  const populateFormWithData = (data) => {
    if (!data) {
      console.error("No data provided to populateFormWithData");
      showNotification("Could not populate form: no data received.", "error");
      return;
    }
    setProfileId(data.id || data.resume_id);
    setProfileData((prevData) => {
      const newData = createProfileDataFromResponse(data, prevData);
      return newData;
    });
  };

  // eslint-disable-next-line no-unused-vars
  const handleResetForm = () => {
    setProfileData(initialProfileState);
    clearLocalStorage(profileId);
    setProfileId(null);
    setIsSameAddress(false);
    setActiveStep(0);
    setValidationErrors({});
    showNotification("Form reset successfully!", "info");
  };

  const handleInputChange = (field, value) => {
    setProfileData((prev) => ({ ...prev, [field]: value }));
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleFileChange = (field, file) => {
    setProfileData((prev) => ({ ...prev, [field]: file }));
  };

  const handleArrayChange = (field, value) => {
    setProfileData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddItem = (field, item) => {
    handleArrayChange(field, [...profileData[field], item]);
  };

  const handleDeleteItem = (field, index) => {
    handleArrayChange(
      field,
      profileData[field].filter((_, i) => i !== index)
    );
  };

  const handleSameAddressToggle = (e) => {
    setIsSameAddress(e.target.checked);
    handleInputChange("isSameAddress", e.target.checked);
  };

  const handleFileRemove = (fileType) => {
    handleInputChange("ui_documents", {
      ...profileData.ui_documents,
      [fileType]: null,
      [`${fileType}_url`]: "",
    });
    showNotification(
      `${
        fileType.charAt(0).toUpperCase() + fileType.slice(1)
      } removed. Save to confirm.`,
      "info"
    );
  };

  const validateCurrentStep = () => {
    const errors = validateStep(activeStep, profileData);
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // In ProfileBuilder.js, replace the entire handleSave function

  const handleSave = async () => {
    if (!validateCurrentStep()) {
      showNotification(
        "Please fix the validation errors before saving.",
        "error"
      );
      return;
    }

    setIsSaving(true);
    showNotification("Saving your progress...", "info");

    const formData = new FormData();

    const dataToSend = JSON.parse(
      JSON.stringify(profileData, (key, value) => {
        if (value instanceof File || key === "photoPreview") {
          return undefined;
        }
        return value;
      })
    );

    if (dataToSend.is_fresher) {
      dataToSend.experience = [];
      dataToSend.internships = [];
    }

    if (dataToSend.experience && Array.isArray(dataToSend.experience)) {
      dataToSend.experience = dataToSend.experience.map((exp) => {
        const { company, responsibilities, ...rest } = exp;
        const responsibilitiesString = Array.isArray(responsibilities)
          ? responsibilities.join("\n")
          : responsibilities || "";
        return {
          ...rest,
          company_name: company,
          responsibilities: responsibilitiesString,
        };
      });
    }

    if (dataToSend.internships && Array.isArray(dataToSend.internships)) {
      dataToSend.internships = dataToSend.internships.map((intern) => {
        const { company, responsibilities, ...rest } = intern;
        const responsibilitiesString = Array.isArray(responsibilities)
          ? responsibilities.join("\n")
          : responsibilities || "";
        return {
          ...rest,
          company_name: company,
          responsibilities: responsibilitiesString,
        };
      });
    }

    if (profileData.ui_documents.resume instanceof File) {
      formData.append("resume_pdf", profileData.ui_documents.resume);
    }
    if (profileData.ui_documents.portfolio instanceof File) {
      formData.append("portfolio_pdf", profileData.ui_documents.portfolio);
    }
    if (profileData.profile_photo instanceof File) {
      formData.append("profile_photo", profileData.profile_photo);
    }

    const processEducationFiles = (field) => {
      if (profileData[field] && Array.isArray(profileData[field])) {
        profileData[field].forEach((item, index) => {
          if (item.certificate && item.certificate instanceof File) {
            formData.append(
              `${field}[${index}][certificate]`,
              item.certificate,
              item.certificate.name
            );
          }
        });
      }
    };
    processEducationFiles("degree_details");
    processEducationFiles("post_graduate_details");
    processEducationFiles("diploma_details");

    if (dataToSend.current_pincode) {
      dataToSend.current_district = `${dataToSend.current_district || ""}-${
        dataToSend.current_pincode
      }`.trim();
    }
    delete dataToSend.current_pincode;
    if (dataToSend.permanent_pincode) {
      dataToSend.permanent_district = `${dataToSend.permanent_district || ""}-${
        dataToSend.permanent_pincode
      }`.trim();
    }
    delete dataToSend.permanent_pincode;

    dataToSend.education_details = {
      tenth: {
        board_name: dataToSend.tenth_board_name || "",
        school_name: dataToSend.tenth_school_name || "",
        year_passing: dataToSend.tenth_year_passing || "",
        score: dataToSend.tenth_score || "",
      },
      twelfth: {
        board_name: dataToSend.twelfth_board_name || "",
        college_name: dataToSend.twelfth_college_name || "",
        year_passing: dataToSend.twelfth_year_passing || "",
        score: dataToSend.twelfth_score || "",
      },
    };

    delete dataToSend.tenth_board_name;
    delete dataToSend.tenth_school_name;
    delete dataToSend.tenth_year_passing;
    delete dataToSend.tenth_score;
    delete dataToSend.twelfth_board_name;
    delete dataToSend.twelfth_college_name;
    delete dataToSend.twelfth_year_passing;
    delete dataToSend.twelfth_score;

    if (
      dataToSend.certification_details &&
      Array.isArray(dataToSend.certification_details)
    ) {
      const formattedCerts = dataToSend.certification_details.map((cert) => ({
        name: cert.name,
        issuing_organization: cert.issuer,
        date_issued: cert.date,
        photo_urls: cert.photo_urls || [],
      }));
      dataToSend.certification_details = formattedCerts;
    }

    const frameworkNames = (dataToSend.frameworks_tools || []).map(
      (item) => item.name
    );
    const combinedSkills = [
      ...new Set([...(dataToSend.skills || []), ...frameworkNames]),
    ];
    dataToSend.skills = combinedSkills;
    delete dataToSend.frameworks_tools;

    delete dataToSend.ui_documents;
    delete dataToSend.profile_photo;
    delete dataToSend.uploaded_documents;

    Object.keys(dataToSend).forEach((key) => {
      const value = dataToSend[key];
      if (value === null || value === undefined) return;
      if (typeof value === "boolean") {
        formData.append(key, value ? 1 : 0);
      } else if (
        Array.isArray(value) ||
        (typeof value === "object" && value !== null)
      ) {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, value);
      }
    });

    try {
      const requestConfig = {
        headers: { "Content-Type": "multipart/form-data" },
      };
      if (profileId) {
        await apiClient.put(`/resume-builder/`, formData, requestConfig);
      } else {
        await apiClient.post("/resume-builder/", formData, requestConfig);
      }

      // --- MODIFIED LOGIC STARTS HERE ---
      // Re-fetch progress to get the most up-to-date value after saving.
      const progressResponse = await apiClient.get("/resume-progress/");
      const latestProgress = progressResponse.data;
      if (latestProgress) {
        setProfileProgress(latestProgress);
      }

      // Check if this is the final submission step
      if (activeStep === steps.length - 1) {
        // Check if the profile is 100% complete
        if (latestProgress?.progress_percentage === 100) {
          clearLocalStorage(profileId);
          setIsCompletionModalOpen(true); // Open the modal instead of navigating
        } else {
          showNotification(
            "Profile saved, but some required sections are still incomplete. Please review all steps.",
            "warning"
          );
        }
      } else {
        // This is just a "Save Progress" click on an earlier step
        showNotification(
          "Progress saved successfully! Refreshing form...",
          "success"
        );
        setTimeout(() => {
          loadExistingProfile();
          scrollToTop();
        }, 2000);
      }
      // --- MODIFIED LOGIC ENDS HERE ---
    } catch (error) {
      console.error("Save error:", error);
      const errorData = error.response?.data;
      let errorMessage = "An error occurred while saving.";
      if (typeof errorData === "object" && errorData !== null) {
        errorMessage = Object.entries(errorData)
          .map(
            ([field, messages]) =>
              `${field}: ${
                Array.isArray(messages) ? messages.join(" ") : messages
              }`
          )
          .join(" | ");
      } else if (typeof errorData === "string") {
        errorMessage = errorData;
      }
      showNotification(errorMessage, "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCurrentExperienceChange = (field, value) =>
    setCurrentExperience((prev) => ({ ...prev, [field]: value }));

  const handleCurrentInternshipChange = (field, value) =>
    setCurrentInternship((prev) => ({ ...prev, [field]: value }));

  const handleAddResponsibility = () => {
    if (newResponsibility) {
      handleCurrentExperienceChange("responsibilities", [
        ...currentExperience.responsibilities,
        newResponsibility,
      ]);
      setNewResponsibility("");
    }
  };

  const handleAddInternshipResponsibility = () => {
    if (newInternshipResponsibility) {
      handleCurrentInternshipChange("responsibilities", [
        ...currentInternship.responsibilities,
        newInternshipResponsibility,
      ]);
      setNewInternshipResponsibility("");
    }
  };

  const handleRemoveResponsibility = (index) =>
    handleCurrentExperienceChange(
      "responsibilities",
      currentExperience.responsibilities.filter((_, i) => i !== index)
    );

  const handleRemoveInternshipResponsibility = (index) =>
    handleCurrentInternshipChange(
      "responsibilities",
      currentInternship.responsibilities.filter((_, i) => i !== index)
    );

  // const handleAddOrUpdateExperience = () => {
  //   const errors = {};
  //   if (!currentExperience.title?.trim())
  //     errors.experience_title = "Job Title is required";
  //   if (!currentExperience.company?.trim())
  //     errors.experience_company = "Company Name is required";
  //   if (!currentExperience.duration?.trim())
  //     errors.experience_duration = "Duration is required";

  //   if (Object.keys(errors).length > 0) {
  //     setValidationErrors((prev) => ({ ...prev, ...errors }));
  //     showNotification(
  //       "Please fill all required fields for the experience.",
  //       "error"
  //     );
  //     return;
  //   }

  //   setValidationErrors((prev) => ({
  //     ...prev,
  //     experience_title: null,
  //     experience_company: null,
  //     experience_duration: null,
  //   }));

  //   const newExperienceArray = [...profileData.experience];
  //   if (editingExperienceIndex !== null) {
  //     newExperienceArray[editingExperienceIndex] = currentExperience;
  //   } else {
  //     newExperienceArray.push(currentExperience);
  //   }
  //   handleInputChange("experience", newExperienceArray);
  //   setCurrentExperience(initialExperienceState);
  //   setEditingExperienceIndex(null);
  // };

  // const handleAddOrUpdateInternship = () => {
  //   const errors = {};
  //   if (!currentInternship.title?.trim())
  //     errors.internship_title = "Internship Title is required";
  //   if (!currentInternship.company?.trim())
  //     errors.internship_company = "Company Name is required";
  //   if (!currentInternship.duration?.trim())
  //     errors.internship_duration = "Duration is required";

  //   if (Object.keys(errors).length > 0) {
  //     setValidationErrors((prev) => ({ ...prev, ...errors }));
  //     showNotification(
  //       "Please fill all required fields for the internship.",
  //       "error"
  //     );
  //     return;
  //   }

  //   setValidationErrors((prev) => ({
  //     ...prev,
  //     internship_title: null,
  //     internship_company: null,
  //     internship_duration: null,
  //   }));

  //   const newInternshipArray = [...profileData.internships];
  //   if (editingInternshipIndex !== null) {
  //     newInternshipArray[editingInternshipIndex] = currentInternship;
  //   } else {
  //     newInternshipArray.push(currentInternship);
  //   }
  //   handleInputChange("internships", newInternshipArray);
  //   setCurrentInternship(initialInternshipState);
  //   setEditingInternshipIndex(null);
  // };

  const handleAddOrUpdateExperience = () => {
    const errors = {};
    if (!currentExperience.title?.trim())
      errors.experience_title = "Job Title is required";
    if (!currentExperience.company?.trim())
      errors.experience_company = "Company Name is required";
    if (!currentExperience.duration?.trim())
      errors.experience_duration = "Duration is required";

    if (Object.keys(errors).length > 0) {
      setValidationErrors((prev) => ({ ...prev, ...errors }));
      showNotification(
        "Please fill all required fields for the experience.",
        "error"
      );
      return;
    }

    setValidationErrors((prev) => ({
      ...prev,
      experience_title: null,
      experience_company: null,
      experience_duration: null,
    }));

    // --- FIX STARTS HERE ---
    // Create a final object to be saved, combining currentExperience state with responsibilities from the text area.
    const finalExperience = { ...currentExperience };

    // Check if there is text in the newResponsibility state
    if (newResponsibility) {
      // Split the text by new lines and filter out any empty lines
      finalExperience.responsibilities = newResponsibility
        .split("\n")
        .filter((line) => line.trim() !== "");
    }
    // --- FIX ENDS HERE ---

    const newExperienceArray = [...profileData.experience];
    if (editingExperienceIndex !== null) {
      newExperienceArray[editingExperienceIndex] = finalExperience; // Use the corrected object
    } else {
      newExperienceArray.push(finalExperience); // Use the corrected object
    }
    handleInputChange("experience", newExperienceArray);
    setCurrentExperience(initialExperienceState);
    setNewResponsibility(""); // Also clear the responsibility text area
    setEditingExperienceIndex(null);
  };

  const handleAddOrUpdateInternship = () => {
    const errors = {};
    if (!currentInternship.title?.trim())
      errors.internship_title = "Internship Title is required";
    if (!currentInternship.company?.trim())
      errors.internship_company = "Company Name is required";
    if (!currentInternship.duration?.trim())
      errors.internship_duration = "Duration is required";

    if (Object.keys(errors).length > 0) {
      setValidationErrors((prev) => ({ ...prev, ...errors }));
      showNotification(
        "Please fill all required fields for the internship.",
        "error"
      );
      return;
    }

    setValidationErrors((prev) => ({
      ...prev,
      internship_title: null,
      internship_company: null,
      internship_duration: null,
    }));

    // --- FIX STARTS HERE ---
    // Create a final object to be saved, combining currentInternship state with responsibilities from the text area.
    const finalInternship = { ...currentInternship };

    // Check if there is text in the newInternshipResponsibility state
    if (newInternshipResponsibility) {
      // Split the text by new lines and filter out any empty lines
      finalInternship.responsibilities = newInternshipResponsibility
        .split("\n")
        .filter((line) => line.trim() !== "");
    }
    // --- FIX ENDS HERE ---

    const newInternshipArray = [...profileData.internships];
    if (editingInternshipIndex !== null) {
      newInternshipArray[editingInternshipIndex] = finalInternship; // Use the corrected object
    } else {
      newInternshipArray.push(finalInternship); // Use the corrected object
    }
    handleInputChange("internships", newInternshipArray);
    setCurrentInternship(initialInternshipState);
    setNewInternshipResponsibility(""); // Also clear the responsibility text area
    setEditingInternshipIndex(null);
  };

  const handleEditExperience = (index) => {
    setEditingExperienceIndex(index);
    setCurrentExperience(profileData.experience[index]);
  };

  const handleEditInternship = (index) => {
    setEditingInternshipIndex(index);
    setCurrentInternship(profileData.internships[index]);
  };

  const handleDeleteExperience = (index) =>
    handleDeleteItem("experience", index);

  const handleDeleteInternship = (index) =>
    handleDeleteItem("internships", index);

  const handleDeleteProject = (index) => handleDeleteItem("projects", index);

  const handleLanguageAdd = () => {
    if (currentLanguage && currentLangProficiency) {
      handleInputChange("languages", {
        ...profileData.languages,
        [currentLanguage]: currentLangProficiency,
      });
      setCurrentLanguage("");
      setCurrentLangProficiency("");
    }
  };

  const handleLanguageRemove = (langName) => {
    const newLangs = { ...profileData.languages };
    delete newLangs[langName];
    handleInputChange("languages", newLangs);
  };

  const generateAIProfile = () => {
    const experienceTitle = profileData.experience[0]?.title || "Professional";
    const summaryText = `Experienced ${experienceTitle} with expertise in ${profileData.skills
      .slice(0, 3)
      .join(", ")}. Proficient in tools like ${profileData.skills
      .slice(3, 6)
      .join(", ")}. Fluent in ${Object.keys(profileData.languages).join(
      ", "
    )}. Seeking global opportunities.`;
    handleInputChange("summary", summaryText);
    handleInputChange(
      "generated_summary",
      `AI-generated: ${summaryText.substring(0, 100)}...`
    );
    showNotification("AI summary generated!", "success");
  };

  const handleNext = () => {
    if (!validateCurrentStep()) {
      showNotification(
        "Please fix the validation errors before proceeding.",
        "error"
      );
      return;
    }
    setActiveStep((prev) => prev + 1);
    scrollToTop();
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    scrollToTop();
  };

  const handleStepClick = (stepIndex) => {
    setActiveStep(stepIndex);
    scrollToTop();
  };

  const handleProjectAdd = () => {
    if (projectName && projectDesc) {
      const newProject = {
        name: projectName,
        description: projectDesc,
        url: projectUrl,
      };
      handleInputChange("projects", [...profileData.projects, newProject]);
      setProjectName("");
      setProjectDesc("");
      setProjectUrl("");
      showNotification("Project added locally. Save to confirm.", "info");
    } else {
      showNotification("Project Name and Description are required.", "error");
    }
  };

  const handleSimpleArrayAdd = (field, value) => {
    if (value && !profileData[field]?.includes(value)) {
      handleInputChange(field, [...(profileData[field] || []), value]);
    }
  };

  const handleSimpleArrayRemove = (field, indexToRemove) => {
    handleInputChange(
      field,
      (profileData[field] || []).filter((_, index) => index !== indexToRemove)
    );
  };

  const handleDomainInterestChange = (domain, checked) => {
    const currentInterests = profileData.domain_interest || [];
    if (checked) {
      if (!currentInterests.includes(domain)) {
        handleInputChange("domain_interest", [...currentInterests, domain]);
      }
    } else {
      handleInputChange(
        "domain_interest",
        currentInterests.filter((interest) => interest !== domain)
      );
    }
  };

  const SimpleArrayInput = ({ title, fieldName, icon, data }) => (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Typography
        variant="h6"
        gutterBottom
        sx={{ display: "flex", alignItems: "center", gap: 1 }}
      >
        {icon} {title}
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          mb: 2,
          p: 1,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          minHeight: "52px",
          bgcolor: "action.hover",
        }}
      >
        {(data || []).map((item, index) => (
          <Chip
            key={index}
            label={item}
            onDelete={() => handleSimpleArrayRemove(fieldName, index)}
            size="small"
            variant="outlined"
          />
        ))}
        {(data?.length === 0 || !data) && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ width: "100%", textAlign: "center", p: 1 }}
          >
            No items added.
          </Typography>
        )}
      </Box>
      <TextField
        fullWidth
        label={`Add a new ${title.slice(0, -1).toLowerCase()} and press Enter`}
        size="small"
        onKeyPress={(e) => {
          if (e.key === "Enter" && e.target.value) {
            e.preventDefault();
            handleSimpleArrayAdd(fieldName, e.target.value);
            e.target.value = "";
          }
        }}
      />
    </Paper>
  );

  const preferredLocationOptions = [
    "UAE (Dubai, Abu Dhabi)",
    "USA (Silicon Valley, New York)",
    "UK (London)",
    "Germany (Berlin)",
    "Singapore",
    "Canada (Toronto)",
    "Australia (Sydney)",
    "Remote",
    "Bangalore",
    "Mumbai",
    "Delhi",
    "Pune",
    "Hyderabad",
    "Chennai",
  ];

  const skillOptions = [
    "Python",
    "PyTorch",
    "TensorFlow",
    "Scikit-Learn",
    "Pandas",
    "AWS",
    "GCP",
    "Docker",
    "Kubernetes",
    "MLOps",
    "React",
    "Node.js",
    "JavaScript",
    "SQL",
    "MongoDB",
    "PostgreSQL",
    "Git",
    "Linux",
    "Java",
    "C++",
    "Go",
    "Rust",
  ];

  const languageOptions = [
    "English",
    "Hindi",
    "German",
    "French",
    "Spanish",
    "Marathi",
  ];

  const proficiencyOptions = ["Basic", "Conversational", "Fluent", "Native"];

  const workArrangementOptions = [
    "Onsite",
    "Hybrid",
    "Remote",
    "Any",
    "Freelance",
  ];

  const domainOptions = ["AI Engineer", "Data Science", "Business Analytics"];

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ backgroundColor: theme.palette.mint.main }}>
            <PersonalInfoStep
              profileData={profileData}
              handleInputChange={handleInputChange}
              handleResumeUpload={handleResumeUpload}
              isUploadingResume={isUploadingResume}
              handleFileRemove={handleFileRemove}
              isSameAddress={isSameAddress}
              handleSameAddressToggle={handleSameAddressToggle}
              fetchAddressFromPincode={fetchAddressFromPincode}
              isFetchingPincode={isFetchingPincode}
              validationErrors={validationErrors}
            />
          </Box>
        );
      case 1:
        return (
          <Box
            sx={{
              backgroundColor: theme.palette.mint.main,
              color: theme.palette.primary.main,
            }}
          >
            <EducationStep
              profileData={profileData}
              handleInputChange={handleInputChange}
              handleFileChange={handleFileChange}
              currentDegree={currentDegree}
              setCurrentDegree={setCurrentDegree}
              initialDegreeState={initialDegreeState}
              currentPostGrad={currentPostGrad}
              setCurrentPostGrad={setCurrentPostGrad}
              initialPostGradState={initialPostGradState}
              currentDiploma={currentDiploma}
              setCurrentDiploma={setCurrentDiploma}
              initialDiplomaState={initialDiplomaState}
              handleAddItem={handleAddItem}
              handleDeleteItem={handleDeleteItem}
              showNotification={showNotification}
              DynamicListSection={DynamicListSection}
              validationErrors={validationErrors}
            />
          </Box>
        );
      case 2:
        return (
          <Box sx={{ backgroundColor: theme.palette.mint.main }}>
            <ProjectsStep
              profileData={profileData}
              projectName={projectName}
              setProjectName={setProjectName}
              projectDesc={projectDesc}
              setProjectDesc={setProjectDesc}
              projectUrl={projectUrl}
              setProjectUrl={setProjectUrl}
              handleProjectAdd={handleProjectAdd}
              handleDeleteProject={handleDeleteProject}
            />
          </Box>
        );
      case 3:
        return (
          <Box sx={{ bgcolor: "background.paper", p: { xs: 1, sm: 2 }, borderRadius: "16px" }}>

            <ProfessionalDetailsStep
              profileData={profileData}
              handleInputChange={handleInputChange}
              currentExperience={currentExperience}
              handleCurrentExperienceChange={handleCurrentExperienceChange}
              currentInternship={currentInternship}
              handleCurrentInternshipChange={handleCurrentInternshipChange}
              editingExperienceIndex={editingExperienceIndex}
              editingInternshipIndex={editingInternshipIndex}
              newResponsibility={newResponsibility}
              setNewResponsibility={setNewResponsibility}
              newInternshipResponsibility={newInternshipResponsibility}
              setNewInternshipResponsibility={setNewInternshipResponsibility}
              handleAddResponsibility={handleAddResponsibility}
              handleAddInternshipResponsibility={
                handleAddInternshipResponsibility
              }
              handleRemoveResponsibility={handleRemoveResponsibility}
              handleRemoveInternshipResponsibility={
                handleRemoveInternshipResponsibility
              }
              handleAddOrUpdateExperience={handleAddOrUpdateExperience}
              handleAddOrUpdateInternship={handleAddOrUpdateInternship}
              handleEditExperience={handleEditExperience}
              handleEditInternship={handleEditInternship}
              handleDeleteExperience={handleDeleteExperience}
              handleDeleteInternship={handleDeleteInternship}
              validationErrors={validationErrors}
            />
          </Box>
        );
      case 4:
        return (
          <Box sx={{ bgcolor: "background.paper", p: { xs: 1, sm: 2 }, borderRadius: "16px" }}>

            <SkillsCertsStep
              profileData={profileData}
              handleInputChange={handleInputChange}
              currentFrameworkTool={currentFrameworkTool}
              setCurrentFrameworkTool={setCurrentFrameworkTool}
              initialFrameworkToolState={initialFrameworkToolState}
              currentCertification={currentCertification}
              setCurrentCertification={setCurrentCertification}
              initialCertificationState={initialCertificationState}
              currentLanguage={currentLanguage}
              setCurrentLanguage={setCurrentLanguage}
              currentLangProficiency={currentLangProficiency}
              setCurrentLangProficiency={setCurrentLangProficiency}
              handleAddItem={handleAddItem}
              handleDeleteItem={handleDeleteItem}
              handleLanguageAdd={handleLanguageAdd}
              handleLanguageRemove={handleLanguageRemove}
              showNotification={showNotification}
              DynamicListSection={DynamicListSection}
              skillOptions={skillOptions}
              languageOptions={languageOptions}
              proficiencyOptions={proficiencyOptions}
              validationErrors={validationErrors}
            />
          </Box>
        );
      case 5:
        return (
          <Box sx={{ bgcolor: "background.paper", p: { xs: 1, sm: 2 }, borderRadius: "16px" }}>

            <AchievementsStep
              profileData={profileData}
              handleInputChange={handleInputChange}
              currentPublication={currentPublication}
              setCurrentPublication={setCurrentPublication}
              initialPublicationState={initialPublicationState}
              handleAddItem={handleAddItem}
              handleDeleteItem={handleDeleteItem}
              handleSimpleArrayAdd={handleSimpleArrayAdd}
              handleSimpleArrayRemove={handleSimpleArrayRemove}
              showNotification={showNotification}
              DynamicListSection={DynamicListSection}
              SimpleArrayInput={SimpleArrayInput}
            />
          </Box>
        );
      case 6:
        return (
          <Box sx={{ bgcolor: "background.paper", p: { xs: 1, sm: 2 }, borderRadius: "16px" }}>

            <PreferencesStep
              profileData={profileData}
              handleInputChange={handleInputChange}
              currentWorkPreference={currentWorkPreference}
              setCurrentWorkPreference={setCurrentWorkPreference}
              initialWorkPreferenceState={initialWorkPreferenceState}
              currentWorkAuth={currentWorkAuth}
              setCurrentWorkAuth={setCurrentWorkAuth}
              initialWorkAuthState={initialWorkAuthState}
              handleAddItem={handleAddItem}
              handleDeleteItem={handleDeleteItem}
              handleDomainInterestChange={handleDomainInterestChange}
              showNotification={showNotification}
              DynamicListSection={DynamicListSection}
              SimpleArrayInput={SimpleArrayInput}
              preferredLocationOptions={preferredLocationOptions}
              workArrangementOptions={workArrangementOptions}
              domainOptions={domainOptions}
              validationErrors={validationErrors}
            />
          </Box>
        );
      case 7:
        return (
          <Box sx={{ bgcolor: "background.paper", p: { xs: 1, sm: 2 }, borderRadius: "16px" }}>

            <DocsLinksStep
              profileData={profileData}
              handleInputChange={handleInputChange}
              handleFileChange={handleFileChange}
              handleFileRemove={handleFileRemove}
              currentLink={currentLink}
              setCurrentLink={setCurrentLink}
              initialLinkState={initialLinkState}
              handleAddItem={handleAddItem}
              handleDeleteItem={handleDeleteItem}
              showNotification={showNotification}
              DynamicListSection={DynamicListSection}
            />
          </Box>
        );
      case 8:
        return (
          <Box sx={{ bgcolor: "background.paper", p: { xs: 1, sm: 2 }, borderRadius: "16px" }}>

            <AISummaryStep
              profileData={profileData}
              handleInputChange={handleInputChange}
              generateAIProfile={generateAIProfile}
            />
          </Box>
        );
      default:
        return <Typography>Unknown Step</Typography>;
    }
  };

  if (isLoading) {
    return (
      <Layout title="AI Talent Profile Builder">
        <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3 } }}>
          <Stack spacing={{ xs: 3, sm: 4 }}>
            <Card
              elevation={0}
              sx={{
                p: 2.5,
                background: theme => `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${theme.palette.background.paper} 100%)`,
                borderRadius: "24px",
                border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >

              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                spacing={2}
              >
                <Box>
                  <Skeleton variant="text" width={250} height={40} />
                  <Skeleton variant="text" width={200} height={20} />
                </Box>
                <Skeleton
                  variant="rectangular"
                  width={180}
                  height={40}
                  sx={{ borderRadius: "50px" }}
                />
              </Stack>
              <Box sx={{ mt: 2 }}>
                <Skeleton
                  variant="rectangular"
                  height={8}
                  sx={{ borderRadius: 5 }}
                />
                <Skeleton
                  variant="text"
                  width={150}
                  height={20}
                  sx={{ mt: 1 }}
                />
              </Box>
            </Card>

            <Paper
              elevation={0}
              sx={{
                p: { xs: 1, sm: 2 },
                bgcolor: "background.paper",
                borderRadius: "24px",
                border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              }}
            >

              <Stack
                direction={isTabletOrSmaller ? "column" : "row"}
                spacing={isTabletOrSmaller ? 2 : 0}
                alignItems="center"
                justifyContent="space-around"
              >
                {[...Array(steps.length)].map((_, index) => (
                  <Stack
                    key={index}
                    alignItems="center"
                    spacing={1}
                    sx={{ width: isTabletOrSmaller ? "100%" : "auto" }}
                  >
                    <Skeleton variant="circular" width={24} height={24} />
                    <Skeleton
                      variant="text"
                      width={isTabletOrSmaller ? 120 : 80}
                      height={20}
                    />
                  </Stack>
                ))}
              </Stack>
            </Paper>

            <Card elevation={2}>
              <CardContent
                sx={{
                  p: { xs: 3, sm: 4, md: 5 },
                  bgcolor: "background.paper",
                }}
              >
               <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

                <Box mb={3}>
                  <Skeleton variant="text" width={300} height={48} />
                  <Divider />
                </Box>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Skeleton variant="rectangular" height={56} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Skeleton variant="rectangular" height={56} />
                  </Grid>
                  <Grid item xs={12}>
                    <Skeleton variant="rectangular" height={56} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Skeleton variant="rectangular" height={56} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Skeleton variant="rectangular" height={56} />
                  </Grid>
                  <Grid item xs={12}>
                    <Skeleton variant="rectangular" height={120} />
                  </Grid>
                </Grid>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    pt: 4,
                    mt: 4,
                    borderTop: 1,
                    borderColor: "divider",
                  }}
                >
                  <Skeleton
                    variant="rectangular"
                    width={80}
                    height={36}
                    sx={{ mr: 1 }}
                  />
                  <Box sx={{ flex: "1 1 auto" }} />
                  <Skeleton
                    variant="rectangular"
                    width={120}
                    height={36}
                    sx={{ mr: 1 }}
                  />
                  <Skeleton variant="rectangular" width={80} height={36} />
                </Box>
               </Box>
              </CardContent>
            </Card>
          </Stack>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title="AI Talent Profile Builder">
      <Container
        maxWidth="xl"
        sx={{
          py: { xs: 2, sm: 3 },
          width: "100%",
          boxSizing: "border-box",
        }}
        ref={formRef}
      >
        <Card
          elevation={0}
          sx={{
            p: 3,
            mb: 4,
            background: theme => theme.palette.mode === 'dark' 
              ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`
              : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${theme.palette.background.paper} 100%)`,
            borderRadius: "24px",
            border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            boxShadow: theme => `0 8px 32px ${alpha(theme.palette.common.black, 0.05)}`,
          }}
        >

          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={2}
          >
            <Box>
              <Typography
                sx={{
                  color: "primary.main",
                  fontWeight: 600,
                  fontSize: { xs: "1.25rem", sm: "1.5rem" },
                }}
              >
                Build your Global AI Profile
              </Typography>
              <Typography sx={{ color: "text.disabled", fontSize: "0.9rem" }}>
                {" "}
                {profileProgress.message}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="primary"
              sx={{
                borderRadius: "50px",
                textTransform: "none",
                fontWeight: 500,
                padding: "6px 24px",
              }}
            >
              Profile Complete
            </Button>
          </Stack>
          <Box sx={{ mt: 2 }}>
            <LinearProgress
              variant="determinate"
              value={profileProgress.progress_percentage || 0}
              sx={{
                height: 10,
                borderRadius: 5,
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                "& .MuiLinearProgress-bar": {
                  background: `linear-gradient(90deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                  borderRadius: 5,
                },
              }}
            />

            <Typography
              sx={{ color: "text.disabled", mt: 1, fontSize: "0.875rem" }}
            >
              {" "}
              {profileProgress.progress_percentage || 0}% Profile Completed
            </Typography>
          </Box>
        </Card>

        <Paper
          elevation={0}
          sx={{
            mb: { xs: 3, sm: 4 },
            p: { xs: 1, sm: 2 },
            bgcolor: "background.paper",
            borderRadius: "24px",
            boxShadow: theme => `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`,
            border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >

          <Stepper
            activeStep={activeStep}
            alternativeLabel={!isMobile}
            orientation={isMobile ? "vertical" : "horizontal"}
            sx={{
              // Target each individual step container
              "& .MuiStep-root": {
                // On desktop, force each step to take equal space and allow it to shrink
                flex: isMobile ? "none" : 1,
                minWidth: 0, // Allows flex item to shrink below its content size
              },
              "& .MuiStepConnector-line": {
                borderColor: "divider",
                borderTopWidth: "1px",
              },
              "& .Mui-active .MuiStepConnector-line, &.Mui-completed .MuiStepConnector-line":
                {
                  borderColor: "primary.main",
                },
            }}
          >
            {steps.map((label, index) => {
              const isMissing = profileProgress.missing_sections?.some(
                (missingSection) =>
                  label.toLowerCase().includes(missingSection.toLowerCase())
              );

              return (
                <Step key={label} completed={activeStep > index}>
                  <StepLabel
                    onClick={() => handleStepClick(index)}
                    StepIconProps={{
                      sx: {
                        color: "text.disabled",
                        "&.Mui-active, &.Mui-completed": {
                          color: "primary.main",
                        },
                        "& .MuiStepIcon-text": {
                          fill: "primary.contrastText",
                        },
                      },
                    }}
                    sx={{
                      cursor: "pointer",
                      // Target the text label itself
                      "& .MuiStepLabel-label": {
                        fontWeight: "bold",
                        textAlign: "center", // Center text for clean wrapping
                        fontSize: {
                          xs: "0.875rem",
                          md: "0.8rem",
                          lg: "0.875rem",
                        }, // Responsive font size
                        mt: 1, // Add some space between icon and label
                        color:
                          isMissing &&
                          activeStep !== index &&
                          !(activeStep > index)
                            ? theme.palette.warning.dark
                            : "text.primary",
                        "&.Mui-active, &.Mui-completed": {
                          color: "primary.main",
                        },
                        "&::after":
                          isMissing &&
                          activeStep !== index &&
                          !(activeStep > index)
                            ? {
                                content: '"*"',
                                color: theme.palette.error.main,
                                marginLeft: "4px",
                                fontWeight: "bold",
                              }
                            : {},
                      },
                    }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </Paper>

        <Card
          elevation={0}
          sx={{
            p: 0,
            border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
            borderRadius: "24px",
            overflow: "hidden",
          }}
        >
          <CardContent
            sx={{
              p: { xs: 3, sm: 4, md: 5 },
              bgcolor: "background.paper",
            }}
          >
            <Box mb={3}>
              <Typography
                variant="h5"
                gutterBottom
                sx={{ fontWeight: 800, color: "primary.main" }}
              >
                {steps[activeStep]}
              </Typography>
              <Divider sx={{ borderColor: (theme) => alpha(theme.palette.primary.main, 0.15) }} />
            </Box>
            {renderStepContent(activeStep)}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                pt: 4,
                mt: 4,
                borderTop: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                gap: 1,
              }}
            >
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                variant="outlined"
                sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 700, px: 3 }}
              >
                Back
              </Button>
              <Box sx={{ flex: "1 1 auto" }} />
              <Button
                onClick={handleSave}
                variant="outlined"
                sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 700, px: 3 }}
                disabled={isSaving}
              >
                {isSaving ? <CircularProgress size={24} /> : "Save Progress"}
              </Button>
              <Button
                variant="contained"
                onClick={
                  activeStep === steps.length - 1 ? handleSave : handleNext
                }
                disabled={isSaving}
                sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 700, px: 3 }}
              >
                {activeStep === steps.length - 1 ? "Submit Profile" : "Next"}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>

      <Dialog
        open={isCompletionModalOpen}
        onClose={() => {
          setIsCompletionModalOpen(false);
          navigate("/gatep/talent");
        }}
        aria-labelledby="profile-completion-dialog-title"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          color="primary"
          id="profile-completion-dialog-title"
          sx={{ textAlign: "center", fontWeight: 900, fontSize: "1.4rem" }}
        >
          Profile Submitted Successfully! 🎉
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
              border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              p: 3,
              borderRadius: "16px",
            }}
          >
            <DialogContentText textAlign="center" mb={4}>
              Your AI Talent Profile is complete. What would you like to do
              next?
            </DialogContentText>
            <Box
              sx={{
                display: "flex",

                flexDirection: { xs: "column", md: "row" },

                gap: 3,
              }}
            >
              {/* Card 1: Mock Interview */}
              <Card elevation={0} sx={{ flex: 1, border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.15)}`, borderRadius: "16px", transition: "all 0.25s ease", "&:hover": { border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.4)}`, transform: "translateY(-3px)" } }}>
                {" "}
                <CardContent sx={{ textAlign: "center", p: 3 }}>
                  <Quiz color="primary" sx={{ fontSize: 40, mb: 2 }} />
                  <Typography variant="h6" gutterBottom fontWeight={800}>
                    Generate Your Skills Passport
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    Give a mock interview to validate your skills and create a
                    shareable passport for employers.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => navigate("/gatep/talent/exam-intro")}
                    sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 700 }}
                  >
                    Start Mock Interview
                  </Button>
                </CardContent>
              </Card>

              {/* Card 2: AI Assistant */}
              <Card elevation={0} sx={{ flex: 1, border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.15)}`, borderRadius: "16px", transition: "all 0.25s ease", "&:hover": { border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.4)}`, transform: "translateY(-3px)" } }}>
                {" "}
                <CardContent sx={{ textAlign: "center", p: 3 }}>
                  <SmartToy color="primary" sx={{ fontSize: 40, mb: 2 }} />
                  <Typography variant="h6" gutterBottom fontWeight={800}>
                    Explore with AI Assistant
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    Use our AI-powered career assistant to get personalized
                    insights and guidance based on your new profile.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => navigate("/gatep/talent/assistant")}
                    sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 700 }}
                  >
                    Go to Assistant
                  </Button>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", p: 2, pt: 3 }}>
          <Button
            onClick={() => {
              setIsCompletionModalOpen(false);
              navigate("/gatep/talent");
            }}
            color="primary"
          >
            Maybe Later
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default ProfileBuilder;
